import { Capacitor } from "@capacitor/core";
import { ECUADOR_MAP_PACKS, mapPackById } from "../../shared/ecuador-map-packs";
import { hasStoredMapPack } from "./offline-maps";
import {
  getMapLabelIndexSummary,
  loadMapLabelTuples,
  scheduleDownloadedMapLabelIndexes,
  scheduleMapLabelIndexForPack,
} from "./android-map-label-index";
import {
  normalizePlaceQuery,
  searchGazetteerTuples,
  type GazetteerTuple,
} from "./place-search-core";

export type AndroidPlaceSearchResult = {
  id: string;
  name: string;
  label: string;
  locality: string;
  region: string;
  country: string;
  kind: string;
  longitude: number;
  latitude: number;
  mapPackId?: string;
};

type SearchOutcome = {
  results: AndroidPlaceSearchResult[];
  message: string;
  source: "online" | "offline" | "offline-fallback" | "local-online" | "map-index" | "map-index-building";
};

type GazetteerPayload = {
  schemaVersion?: number;
  source?: string;
  attribution?: string;
  places?: GazetteerTuple[];
};

const ANDROID_GEOCODE_BASE = "https://thera-rutas-ecuador.pages.dev";
const LOCAL_GAZETTEER_URL = "/search/ecuador-gazetteer.json";
const ANDROID_PACKS = ECUADOR_MAP_PACKS.filter((pack) => !pack.overview);
let gazetteerPromise: Promise<GazetteerTuple[]> | null = null;

function humanPackName(mapPackId?: string | null) {
  return mapPackById(mapPackId)?.shortName ?? "Ecuador";
}

function fromGazetteerMatch(match: ReturnType<typeof searchGazetteerTuples>[number]): AndroidPlaceSearchResult {
  const [id, name, , latitude, longitude, kind] = match.tuple;
  const mapPackId = match.mapPackId ?? undefined;
  const region = humanPackName(mapPackId);
  return {
    id,
    name,
    label: `${name} · ${kind} · ${region}`,
    locality: "",
    region,
    country: "Ecuador",
    kind,
    longitude,
    latitude,
    mapPackId,
  };
}

function localAssetUrl() {
  if (typeof document !== "undefined" && document.baseURI) {
    return new URL(LOCAL_GAZETTEER_URL.replace(/^\//, ""), document.baseURI).toString();
  }
  return LOCAL_GAZETTEER_URL;
}

async function loadGazetteer() {
  if (!gazetteerPromise) {
    gazetteerPromise = fetch(localAssetUrl(), { cache: "force-cache", headers: { Accept: "application/json" } })
      .then(async (response) => {
        if (!response.ok) throw new Error("Índice offline no disponible.");
        const payload = await response.json() as GazetteerPayload;
        if (!Array.isArray(payload.places)) throw new Error("Índice offline inválido.");
        return payload.places;
      })
      .catch((error) => {
        gazetteerPromise = null;
        throw error;
      });
  }
  return gazetteerPromise;
}

async function downloadedPackIds() {
  const states = await Promise.all(ANDROID_PACKS.map(async (pack) => {
    try {
      return [pack.id, await hasStoredMapPack(pack.file)] as const;
    } catch {
      return [pack.id, false] as const;
    }
  }));
  return new Set(states.filter(([, stored]) => stored).map(([id]) => id));
}

function dedupeResults(results: AndroidPlaceSearchResult[], limit = 8) {
  const seen = new Set<string>();
  const output: AndroidPlaceSearchResult[] = [];
  for (const result of results) {
    const key = `${normalizePlaceQuery(result.name)}|${result.latitude.toFixed(4)}|${result.longitude.toFixed(4)}`;
    if (seen.has(key)) continue;
    seen.add(key);
    output.push(result);
    if (output.length >= limit) break;
  }
  return output;
}

async function searchLocalGazetteer(
  query: string,
  options: { offlineOnly: boolean; preferredPackId?: string | null; limit?: number },
) {
  const [places, installed] = await Promise.all([
    loadGazetteer(),
    options.offlineOnly ? downloadedPackIds() : Promise.resolve(new Set<string>()),
  ]);
  const matches = searchGazetteerTuples(places, query, {
    allowedPackIds: options.offlineOnly ? installed : null,
    preferredPackId: options.preferredPackId,
    limit: options.limit ?? 8,
  });
  return { results: matches.map(fromGazetteerMatch), installedCount: installed.size };
}

async function searchMapLabelIndexes(
  query: string,
  preferredPackId?: string | null,
  limit = 8,
) {
  // V4: nunca esperamos a que el mapa entero sea indexado dentro de una búsqueda.
  // La preparación corre en un Web Worker y la consulta usa de inmediato cualquier
  // parte del índice que ya esté persistida.
  if (preferredPackId) void scheduleMapLabelIndexForPack(preferredPackId, true);
  else void scheduleDownloadedMapLabelIndexes(null);

  const installed = await downloadedPackIds();
  if (!installed.size) {
    return {
      results: [] as AndroidPlaceSearchResult[],
      installedCount: 0,
      summary: { building: false, readyCount: 0, indexedCount: 0, progress: 0, mapPackIds: [] as string[] },
    };
  }

  const [places, summary] = await Promise.all([
    loadMapLabelTuples(installed).catch(() => [] as GazetteerTuple[]),
    getMapLabelIndexSummary(installed).catch(() => ({
      building: false,
      readyCount: 0,
      indexedCount: 0,
      progress: 0,
      mapPackIds: [] as string[],
    })),
  ]);
  const matches = searchGazetteerTuples(places, query, {
    allowedPackIds: installed,
    preferredPackId,
    limit,
  });
  return { results: matches.map(fromGazetteerMatch), installedCount: installed.size, summary };
}

export function isNativeAndroidPlaceSearch() {
  return Capacitor.getPlatform() === "android";
}

async function searchOnlineFromAndroid(query: string): Promise<AndroidPlaceSearchResult[]> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch(`${ANDROID_GEOCODE_BASE}/api/geocode?q=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    });
    const contentType = response.headers.get("content-type")?.toLocaleLowerCase() ?? "";
    if (!contentType.includes("application/json")) return [];
    const payload = await response.json() as { results?: AndroidPlaceSearchResult[]; error?: string };
    if (!response.ok) return [];
    return Array.isArray(payload.results) ? payload.results : [];
  } catch {
    return [];
  } finally {
    window.clearTimeout(timeout);
  }
}

function buildingMessage(progress: number, preferredPackId?: string | null) {
  const pack = preferredPackId ? humanPackName(preferredPackId) : "tus mapas";
  const suffix = progress > 0 ? ` (${progress}%)` : "";
  return `THERA está preparando automáticamente los nombres de ${pack}${suffix}. Puedes seguir usando la app y volver a buscar en unos minutos.`;
}

export async function searchAndroidPlaces(
  query: string,
  online: boolean,
  preferredPackId?: string | null,
): Promise<SearchOutcome> {
  const mapped = await searchMapLabelIndexes(query, preferredPackId, 8)
    .catch(() => ({
      results: [] as AndroidPlaceSearchResult[],
      installedCount: 0,
      summary: { building: false, readyCount: 0, indexedCount: 0, progress: 0, mapPackIds: [] as string[] },
    }));

  if (mapped.results.length) {
    return {
      results: mapped.results,
      source: "map-index",
      message: `${mapped.results.length} ${mapped.results.length === 1 ? "lugar encontrado" : "lugares encontrados"} en los nombres de tus mapas THERA.`,
    };
  }

  if (!online) {
    try {
      const local = await searchLocalGazetteer(query, { offlineOnly: true, preferredPackId });
      if (local.results.length) {
        return {
          results: local.results,
          source: "offline",
          message: `${local.results.length} ${local.results.length === 1 ? "lugar encontrado" : "lugares encontrados"} en tus mapas descargados.`,
        };
      }
      if (!local.installedCount && !mapped.installedCount) {
        return {
          results: [],
          source: "offline",
          message: "Sin conexión. Descarga un mapa regional para buscar sus lugares offline.",
        };
      }
      if (mapped.summary.building) {
        return {
          results: [],
          source: "map-index-building",
          message: buildingMessage(mapped.summary.progress, preferredPackId),
        };
      }
      return {
        results: [],
        source: "offline",
        message: "Sin coincidencias dentro de los mapas que tienes descargados.",
      };
    } catch {
      return {
        results: [],
        source: "offline",
        message: mapped.summary.building
          ? buildingMessage(mapped.summary.progress, preferredPackId)
          : "No encontramos ese nombre dentro de los mapas descargados.",
      };
    }
  }

  // Con red, el índice local del mismo PMTiles sigue siendo la primera fuente.
  // Mientras se prepara, la búsqueda nunca queda bloqueada: continúa de inmediato
  // con el catálogo nacional y el geocodificador publicado.
  const localOutcome = await searchLocalGazetteer(query, { offlineOnly: false, preferredPackId, limit: 8 })
    .catch(() => ({ results: [] as AndroidPlaceSearchResult[], installedCount: 0 }));
  if (localOutcome.results.length) {
    return {
      results: localOutcome.results,
      source: "local-online",
      message: `${localOutcome.results.length} ${localOutcome.results.length === 1 ? "lugar encontrado" : "lugares encontrados"} en el catálogo nacional THERA.`,
    };
  }

  const remoteResults = await searchOnlineFromAndroid(query);
  const results = dedupeResults(remoteResults, 8);
  if (results.length) {
    return {
      results,
      source: "online",
      message: `${results.length} ${results.length === 1 ? "lugar encontrado" : "lugares encontrados"}.`,
    };
  }

  if (mapped.summary.building) {
    return {
      results: [],
      source: "map-index-building",
      message: buildingMessage(mapped.summary.progress, preferredPackId),
    };
  }

  return {
    results: [],
    source: "offline-fallback",
    message: "No encontramos ese nombre. Prueba otra escritura o un nombre cercano.",
  };
}
